import classNames from 'classnames';
import {
  type ComponentProps,
  type ComponentPropsWithRef,
  type FC,
  type ReactElement,
  type ReactNode,
  useId,
} from 'react';
import { FieldFooter } from '../../atoms/field-footer';
import { ChevronDownXs } from '../../icons/arrows/chevron-down-xs';
import { ExclamationMarkTriangleXs } from '../../icons/symbols/exclamation-mark-triangle-xs';
import { LabelSuffix } from '../helpers/label-suffix';
import styles from './select.module.css';

export type SelectProps = Omit<ComponentPropsWithRef<'select'>, 'type'> & {
  children?: ReactElement<typeof Option>[];
  className?: string;
  'data-testid'?: string;
  emptyOptionLabel?: string;
  errorMessage?: string;
  footer?: ReactNode;
  label: string;
  hideLabelSuffix?: boolean;
};

type SelectComponent = FC<SelectProps> & {
  Option: typeof Option;
};

const Option: FC<ComponentProps<'option'>> = ({ ...props }) => <option {...props} />;

/**
 * `Select` is a customizable select component with integrated labeling, optional footer, and error message support.
 *
 * @property {ReactElement<typeof Option>[]} [children] - The option elements to be rendered inside the select.
 * @property {string} label - The text label describing the select field.
 * @property {string} [className] - Additional CSS classes for the wrapper element.
 * @property {string} [emptyOptionLabel] - Label shown as the only option to prompt the user. When provided, the select appears visually disabled, blocking user interaction, but still allows native form validation to work.
 * @property {string} [errorMessage] - An optional error message displayed below the select input.
 * @property {ReactNode} [footer] - An optional footer for additional help text.
 *
 * @example
 * ```tsx
 * <Select
 *   label="Select your country"
 *   footer="Please choose carefully."
 *   errorMessage="This field is required."
 *   value={selectedCountry}
 *   onChange={handleCountryChange}
 * >
 *   <Select.Option value="de">Germany</Select.Option>
 *   <Select.Option value="us">United States</Select.Option>
 * </Select>
 * ```
 */
export const Select: SelectComponent = ({
  children,
  className,
  'data-testid': testId,
  emptyOptionLabel,
  errorMessage,
  footer,
  label,
  ...props
}) => {
  const id = useId();
  const labelId = `${id}-label`;
  const footerId = footer ? `${id}-footer` : undefined;

  return (
    <div className={classNames(styles.wrapper, className)} data-testid={testId}>
      <label className={classNames(styles.label, props.disabled && styles['label-disabled'])} htmlFor={id} id={labelId}>
        {label}
        {!props.hideLabelSuffix && <LabelSuffix required={props.required} disabled={props.disabled} />}
      </label>
      <select
        className={styles.select}
        id={id}
        {...props}
        aria-disabled={Boolean(emptyOptionLabel)}
        aria-labelledby={[labelId, footerId].filter(Boolean).join(' ')}
      >
        <button type="button">
          {/* @ts-expect-error */}
          <selectedcontent />
          <ChevronDownXs />
        </button>
        {emptyOptionLabel ? (
          <Option className={styles['empty-option']} value="">
            {emptyOptionLabel}
          </Option>
        ) : (
          children
        )}
      </select>
      {footer && (
        <FieldFooter className={styles.footer} data-testid="field-footer" id={footerId}>
          {footer}
        </FieldFooter>
      )}
      {errorMessage && (
        <FieldFooter
          className={styles['error-message']}
          data-testid="field-error"
          error
          icon={<ExclamationMarkTriangleXs />}
        >
          {errorMessage}
        </FieldFooter>
      )}
    </div>
  );
};

Select.Option = Option;
